/* eslint-disable no-unused-vars */
/* eslint-disable max-lines */
import {
    fileManagerService
} from 'shared/api/services/BEX/fileManager.service';
import {
    useEffect, useRef, useState
} from 'react';
import {
    useParams
} from 'react-router-dom';
import {
    getCOSFormatDirectoryFromKey,
    mapFinder,
    parentFolderObjectSplitter,
} from './utils/fileUtils';
import {
    fileMapHandler, listRefresher
} from './utils/mapHandlers';
import axios from 'axios';
/**
 * @typedef KBDocument
 * @property {string} filename The name of the document.
 * @property {string?} prefix The prefix of the document.
 * @property {string} uploader The user who uploaded the file.
 * @property {string} permission Access level. (Shared, or Private)
 * @property {string} origin The platform where the file is uploaded.
 * @property {string} l1Status L1 status of the file.
 * @property {string} l2Status L2 status of the file.
 */
export const META_MAP_DIR = '%meta_mapdir%';
/**
 * This hook will handle the file and folder list for Knowledge Builder.
 * @param {string} orgId The organization ID of the organization.
 * @param {string} bucketId The bucket name/ID where the files are stored.
 */
export default function useFileHandler(orgId, bucketId) {
    const [documentMap, setDocumentMap] = useState(
        new Map([['%__parentDir__%', new Map([[META_MAP_DIR, '%__parentDir__%']])]])
    );
    const [metaMap, setMetaMap] = useState(
        new Map([
            [
                '%__parentDir__%',
                {
                    type: 'folder',
                    name: 'Parent Directory',
                    mapDir: '%__parentDir__%',
                    isOpened: true,
                    isSelected: true,
                    level: 0,
                },
            ],
        ])
    );
    const [selectedFolder, setSelectedFolder] = useState(null);
    const [selectedDocument, setSelectedDocument] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const ContinuationToken = useRef(null);
    const isFirstLoading = useRef(true);
    const {
        oragID
    } = useParams();

    useEffect(() => {
    //

        if (isFirstLoading.current) {
            setSelectedFolder(documentMap.get('%__parentDir__%'));
            isFirstLoading.current = false;
        }
    }, [documentMap, metaMap]);

    const handleCheckMetadata = (mapDir, metadataKey) => {
        const mapDirectory = metaMap.get(mapDir);
        try {
            if (!metadataKey) return mapDirectory;
            return mapDirectory[metadataKey];
        } catch (e) {
            console.error(`Error while checking metadata for ${mapDirectory}`, e);
            return null;
        }
    };

    /**
   * Handles the selction of folder.
   * @param {string} mapDir The map directory location. (Separated by "/")
   */
    const objectSelector = (mapDir) => {
        const selectedObjectMap = mapFinder(documentMap, mapDir);
        const objectType = handleCheckMetadata(mapDir, 'type');
        if (objectType === 'folder') {
            setSelectedFolder((prevFolder) => {

                if (!selectedObjectMap) return prevFolder;
                setMetaMap((prevMap) => {
                    const newMeta = new Map(prevMap);

                    if (prevFolder) {
                        const prevFolderDir = prevFolder.get(META_MAP_DIR);
                        // Fix: Use the previous folder's metadata when deselecting
                        newMeta.set(prevFolderDir, {
                            ...newMeta.get(prevFolderDir), // <-- Use previous folder's metadata
                            isSelected: false,
                        });
                    }
                    // Select new folder in the new map
                    newMeta.set(mapDir, {
                        ...newMeta.get(mapDir),
                        isSelected: true,
                        isOpened: true,
                    });

                    return newMeta;
                });

                return selectedObjectMap;
            });
        } else if (objectType === 'file') {
            setSelectedDocument((prevFile) => {

                if (!selectedObjectMap) return prevFile;

                // Create a new document map to maintain immutability
                setMetaMap((prevMap) => {
                    const newMeta = new Map(prevMap);

                    if (prevFile) {
                        // Deselect previous folder in the new map
                        newMeta.set(prevFile.get(META_MAP_DIR), {
                            ...newMeta.get(mapDir),
                            isSelected: false,
                        });
                    }
                    // Select new folder in the new map
                    newMeta.set(mapDir, {
                        ...newMeta.get(mapDir),
                        isSelected: true,
                    });

                    return newMeta;
                });

                return selectedObjectMap;
            });
        } else {
            console.warn('Unknown Knowledge Builder object type.', objectType);
        }
    };
    /**
   * Toggles the folder open/close state
   * @param {string} mapDir The map directory location. (Separated by "/")
   */
    const toggleFolder = (mapDir) => {
        const folderMap = mapFinder(documentMap, mapDir);
        if (!folderMap) return; // Guard clause for invalid mapDir

        const currentMeta = handleCheckMetadata(mapDir);
        const willOpen = !currentMeta.isOpened;

        if (willOpen && folderMap.size <= 1) {

            const s3Path = getCOSFormatDirectoryFromKey(mapDir);
            if (s3Path) {
                loadFolderContents(s3Path);
            }
        }

        setMetaMap((prevMap) => {
            const newMeta = new Map(prevMap);
            const currentMetaInState = newMeta.get(mapDir) || currentMeta;

            newMeta.set(mapDir, {
                ...currentMetaInState,
                isOpened: willOpen,
            });

            return newMeta;
        });
    };
    const handleAddFolder = () => {
        setDocumentMap((prevMap) => {
            const newMap = new Map(prevMap);
            const selectedFolderInMap = mapFinder(
                newMap,
                handleCheckMetadata(selectedFolder.get(META_MAP_DIR), 'mapDir')
            );
            const newFolderName = '%__Unnamed__%';
            const mapDir = selectedFolderInMap.get(META_MAP_DIR);
            const newFolderDir = mapDir + `/${newFolderName}`;
            selectedFolderInMap.set(
                newFolderName,
                new Map([[META_MAP_DIR, newFolderDir]])
            );
            setMetaMap((prevMetaMap) => {
                const newMetaMap = new Map(prevMetaMap);
                const parentLevel = newMetaMap.get(mapDir)?.level;

                newMetaMap.set(newFolderDir, {
                    isSelected: false,
                    level: parentLevel + 1,
                    mapDir: newFolderDir,
                    name: newFolderName,
                    type: 'folder',
                    isNew: true,
                });
                return newMetaMap;
            });

            return newMap;
        });
    };

    /**
   *
   * @param {string[]} fileDirs List of file directory to be deleted.
   */
    const deleteObject = async (fileDirs, fromCancel = false) => {
        if (!fromCancel) {
            const deleteFiles = fileDirs.map((dir) =>
                getCOSFormatDirectoryFromKey(dir)
            );
            const fileOBj = {
                organizationId: oragID,
                bucketId: bucketId,
                files: deleteFiles,
            };
            setIsLoading(true);
            await fileManagerService.deleteFiles(fileOBj);
        }
        setDocumentMap((prevMap) => {
            const newMap = new Map(prevMap);
            fileDirs.forEach((dir) => {
                //
                const {
                    parentDir, objectName
                } = parentFolderObjectSplitter(dir);
                const parentMap = mapFinder(newMap, parentDir);
                parentMap?.delete(objectName);
            });
            return newMap;
        });
    };

    const findFilesInFolder = (folderPath) => {
        const files = Array.from(metaMap.entries())
            .filter(([key, _]) => key.includes(folderPath))
            .map(([key, _]) => key);

        return files;
    };
    const saveFolder = async (mapObject, folderName, userId) => {
        const dirArray = mapObject.get(META_MAP_DIR).split('/');
        const parent = dirArray.slice(0, -1).join('/');
        const objectName = dirArray.slice(-1)[0];
        const newDir = `${parent}/${folderName}`;

        // Create the placeholder file details
        const placeholderFileName = '.keep';
        // Sanitize newDir using utility
        const s3Dir = getCOSFormatDirectoryFromKey(newDir);
        const placeholderKey = s3Dir ? `${s3Dir}/${placeholderFileName}` : placeholderFileName;

        const uploadFilesObj = {
            organizationId: oragID,
            bucketId: bucketId,
            prefix: placeholderKey,
            fileNames: [placeholderFileName],
            metadataArray: [
                JSON.stringify({
                    lastModifiedDate: new Date(),
                    userId: userId,
                    organizationId: orgId, // using orgId from props/scope
                    origin: 'KB_BUILDER'
                })
            ]
        };

        try {

            // Get pre-signed URL
            const response = await fileManagerService.getPreSignedUrls(uploadFilesObj);
            const {
                preSignedUrls
            } = response.data;

            if (preSignedUrls && preSignedUrls.length > 0) {
                const {
                    url
                } = preSignedUrls[0];

                // Upload file
                await axios.put(url, new Blob([''], {
                    type: 'text/plain'
                }), {
                    headers: {
                        'Content-Type': 'text/plain'
                    }
                });

            } else {
                console.error('No pre-signed URLs returned for placeholder');
            }
        } catch (error) {
            console.error('Error creating placeholder file:', error);
            if (error.response) {
                console.error('Error response:', error.response.data);
            }
        }

        setDocumentMap((prevMap) => {
            const newMap = new Map(prevMap);
            const parentMap = mapFinder(newMap, parent);
            parentMap.delete(objectName);
            // Set new object (Saved)
            parentMap.set(folderName, new Map([[META_MAP_DIR, newDir]]));
            // Set Meta for the new
            setMetaMap((prevMetaMap) => {
                const newMetaMap = new Map(prevMetaMap);
                const parentLevel = newMetaMap.get(parent)?.level;
                newMetaMap.set(newDir, {
                    isSelected: false,
                    level: parentLevel + 1,
                    mapDir: newDir,
                    name: folderName,
                    type: 'folder',
                });
                return newMetaMap;
            });

            return newMap;
        });
    };

    const loadDocuments = async (prefix = null, delimiter = '/') => {
        setIsLoading(true);

        return fileManagerService
            .listFilesWithPresignedUrl(
                orgId,
                bucketId,
                prefix,
                true,
                ContinuationToken.current,
                delimiter
            )
            .then((listFileResponse) => {
                const responseData = listFileResponse.data;
                ContinuationToken.current = responseData.next_continuation_token;

                let files = responseData.documents || [];
                if (responseData.CommonPrefixes && Array.isArray(responseData.CommonPrefixes)) {
                    const folderPlaceholders = responseData.CommonPrefixes.map(prefixObj => {
                        const folderPrefix = prefixObj.Prefix;
                        return {
                            Key: `${folderPrefix}.keep`,
                            metadata: {
                                userid: 'system',
                                origin: 'system'
                            }
                        };
                    });
                    files = [...files, ...folderPlaceholders];
                }

                fileMapHandler(
                    files,
                    documentMap,
                    metaMap,
                    setDocumentMap,
                    setMetaMap
                );
            })
            .catch(e => {
                console.error('Error loading documents:', e);
            })
            .finally(() => {
                console.info('Files are loaded');
                setIsLoading(false);
            });
    };

    const loadFolderContents = async (folderPath) => {
        // Ensure folderPath ends with / for prefix query
        const prefix = folderPath.endsWith('/') ? folderPath : `${folderPath}/`;

        await loadDocuments(prefix, '/');
    };
    const refresh = async () => {
        setIsLoading(true);
        // Refresh loads root level lazily
        fileManagerService
            .listFilesWithPresignedUrl(orgId, bucketId, null, true, null, '/')
            .then((listFileResponse) => {
                const responseData = listFileResponse.data;
                ContinuationToken.current = responseData.next_continuation_token;

                // Handle regular files
                let files = responseData.documents || [];

                // Handle Subdirectories (CommonPrefixes)
                if (responseData.CommonPrefixes && Array.isArray(responseData.CommonPrefixes)) {
                    const folderPlaceholders = responseData.CommonPrefixes.map(prefixObj => {
                        const folderPrefix = prefixObj.Prefix;
                        return {
                            Key: `${folderPrefix}.keep`,
                            metadata: {
                                userid: 'system',
                                origin: 'system'
                            }
                        };
                    });
                    files = [...files, ...folderPlaceholders];
                }

                listRefresher(
                    files,
                    selectedFolder,
                    setSelectedFolder,
                    metaMap,
                    setDocumentMap,
                    setMetaMap
                );
            })
            .catch(e => {
                console.error('Error refreshing list:', e);
            })
            .finally(() => {
                console.info('Files are loaded');
                setIsLoading(false);
            });
    };

    const updateFileStatus = (fileMapDirs, status, type = 'l1') => {
        setMetaMap((prevMetaMap) => {
            const newMetaMap = new Map(prevMetaMap);
            fileMapDirs.forEach((mapDir) => {
                let targetKey = mapDir;
                if (!newMetaMap.has(targetKey)) {
                    targetKey = `%__parentDir__%/${mapDir}`;
                }

                const currentMeta = newMetaMap.get(targetKey);
                if (currentMeta) {
                    if (type === 'l1') {
                        newMetaMap.set(targetKey, {
                            ...currentMeta,
                            status: {
                                status: status,
                                message:
                                    status === 'processing'
                                        ? 'Processing...'
                                        : currentMeta.status?.message || '',
                            },
                        });
                    } else if (type === 'l2') {
                        newMetaMap.set(targetKey, {
                            ...currentMeta,
                            l2_status: {
                                status: status,
                                message:
                                    status === 'processing'
                                        ? 'Processing...'
                                        : currentMeta.l2_status?.message || '',
                            },
                        });
                    }
                }
            });
            return newMetaMap;
        });
    };

    const refreshList = async () => {
        refresh()
            .then(() => {
                console.info('Files are refreshed');
            })
            .catch((e) => {
                console.error('Error while refreshing the list', e);
            });
    };
    useEffect(() => {
        if (!orgId || !bucketId) {
            throw new Error(
                'Organization ID and Bucket ID is required to use the file handler hook.'
            );
        }
        loadDocuments(null, '/'); // Initial load: Root only
    }, [orgId, bucketId]);
    return {
        documentMap,
        documentIsLoading: isLoading,
        setDocumentIsLoading: setIsLoading,
        selectedFolder,
        selectedDocument,
        metaMap,
        fileMapHandler: (files) =>
            fileMapHandler(files, documentMap, metaMap, setDocumentMap, setMetaMap),
        objectSelector,
        toggleFolder,
        handleCheckMetadata,
        handleAddFolder,
        deleteObject,
        saveFolder,
        refreshList,
        findFilesInFolder,
        loadDocuments,
        loadFolderContents,
        updateFileStatus,
        ContinuationToken,
    };
}
